import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, User, Phone } from 'lucide-react';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let result;
      if (isLogin) {
        result = await login({
          email: formData.email,
          password: formData.password
        });
      } else {
        result = await register({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          phone: formData.phone
        });
      }

      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Hiba történt. Kérlek próbáld újra.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="relative min-h-screen pt-24 pb-12">
      <canvas id="starfield" className="fixed inset-0 z-0 pointer-events-none"></canvas>
      
      <div className="relative z-10 max-w-md mx-auto px-4 sm:px-6 lg:px-8">
        <div className="glass rounded-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="section-title text-3xl mb-2">
              <span>{isLogin ? 'Bejelentkezés' : 'Regisztráció'}</span>
            </h1>
            <p className="text-gray-400">Folytasd a vásárlást</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 mb-6">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name (Register only) */}
            {!isLogin && (
              <div>
                <label className="block text-sm text-gray-400 mb-2">Név</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                    placeholder="Teljes neved"
                  />
                </div>
              </div>
            )}

            {/* Email */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Email cím</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                  placeholder="pelda@email.com"
                />
              </div>
            </div>

            {/* Phone (Register only) */}
            {!isLogin && (
              <div>
                <label className="block text-sm text-gray-400 mb-2">Telefonszám (opcionális)</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                    placeholder="+36 20 123 4567"
                  />
                </div>
              </div>
            )}

            {/* Password */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Jelszó</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength="6"
                  className="w-full pl-10 pr-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                  placeholder="••••••••"
                />
              </div>
              {!isLogin && (
                <p className="text-xs text-gray-500 mt-1">Legalább 6 karakter</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-magic rounded-lg py-3 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Feldolgozás...' : (isLogin ? 'Bejelentkezés' : 'Regisztráció')}
            </button>
          </form>

          {/* Toggle Login/Register */}
          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
              }}
              className="text-yellow-200 hover:text-yellow-300 transition"
            >
              {isLogin ? 'Még nincs fiókod? Regisztrálj!' : 'Már van fiókod? Jelentkezz be!'}
            </button>
          </div>

          {/* Guest Checkout Note */}
          <div className="mt-6 p-4 bg-yellow-200/5 border border-yellow-200/20 rounded-lg">
            <p className="text-sm text-gray-400 text-center">
              🛍️ Vendégként is vásárolhatsz regisztráció nélkül
            </p>
          </div>
        </div>
      </div>

      <style jsx>{`
        .glass {
          background: linear-gradient(180deg, hsl(0 0% 100% / 0.04), hsl(0 0% 100% / 0.02));
          border: 1px solid hsl(0 0% 100% / 0.06);
          backdrop-filter: blur(6px);
        }
        .section-title {
          font-family: "Great Vibes", cursive;
          text-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
        }
        .section-title span {
          color: #ffffff;
        }
        .btn-magic {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          border: 1px solid hsl(0 0% 100% / 0.14);
        }
        .btn-magic:before {
          content: '';
          position: absolute;
          inset: -200%;
          background: conic-gradient(from 0deg, transparent, hsl(45 86% 62% / 0.25), transparent 30%);
          animation: spin 6s linear infinite;
          z-index: -1;
          filter: blur(12px);
        }
        @keyframes spin {
          to { transform: rotate(1turn); }
        }
      `}</style>
    </div>
  );
};

export default Login;